import Seo from "@/components/Seo";
import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  POSITIONS,
  MARKET_POSITIONS,
  ROADBLOCKS,
} from "@/lib/content";
import { submitPositioningReview } from "@/lib/api";
import { ArrowRight, Check, Loader2, ArrowLeft, Sparkles } from "lucide-react";

const STEPS = [
  { idx: 0, key: "pillar", label: "Known For" },
  { idx: 1, key: "market_position", label: "Position" },
  { idx: 2, key: "roadblock", label: "Roadblock" },
  { idx: 3, key: "lead", label: "Lead Capture" },
];

const labelsFor = (options, values) =>
  options.filter((o) => values.includes(o.value)).map((o) => o.label);

export default function PositioningReview() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    pillar: [],
    market_position: [],
    roadblock: [],
    name: "",
    email: "",
    company: "",
    role: "",
    phone: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const setVal = (k, v) => setData((s) => ({ ...s, [k]: v }));

  const toggle = (k, v) =>
    setData((s) => {
      const arr = s[k];
      return {
        ...s,
        [k]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v],
      };
    });

  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const canProceed = () => {
    if (step === 0) return data.pillar.length > 0;
    if (step === 1) return data.market_position.length > 0;
    if (step === 2) return data.roadblock.length > 0;
    if (step === 3) return data.name && data.email && data.company;
    return false;
  };

  const submit = async () => {
    if (!canProceed()) return;
    setLoading(true);
    try {
      await submitPositioningReview({
        pillar: data.pillar,
        market_position: data.market_position,
        roadblock: data.roadblock,
        name: data.name,
        email: data.email,
        company: data.company,
        role: data.role || undefined,
        phone: data.phone || undefined,
        notes: data.notes || undefined,
      });
      setDone(true);
      toast.success("Positioning Review submitted.");
    } catch (e) {
      toast.error("Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    const pickedPositions = labelsFor(POSITIONS, data.pillar);
    return (
      <main
        data-testid="positioning-success"
        className="dark-section bg-zinc-950 pt-32 pb-20 min-h-screen"
      >
        <div className="mx-auto max-w-3xl px-5 md:px-10 text-center">
          <div className="inline-flex items-center justify-center h-16 w-16 bg-accent-blue text-black mx-auto rounded-full">
            <Check className="h-8 w-8" />
          </div>
          <span className="label-eyebrow text-accent-blue mt-8 block">
            Discovery Call · Booked
          </span>
          <h1 className="font-heading font-black text-5xl md:text-7xl mt-4 leading-[0.95] tracking-tighter">
            Thanks, we&apos;ll be in touch.
          </h1>
          <p className="mt-6 text-lg text-zinc-300 leading-relaxed">
            We&apos;ll come back to you within 2 working days with a tailored
            plan for being known as{" "}
            <span className="text-accent-blue font-bold">
              {pickedPositions.length
                ? pickedPositions.join(", ")
                : "your chosen position"}
            </span>
            .
          </p>

          {/* Custom-packages reassurance */}
          <div
            data-testid="success-custom-packages"
            className="mt-12 border border-zinc-800 bg-zinc-900/40 rounded-2xl p-8 md:p-10 text-left"
          >
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center h-9 w-9 bg-accent-blue/15 text-accent-blue rounded-full">
                <Sparkles className="h-4 w-4" />
              </span>
              <span className="label-eyebrow text-accent-blue">
                Built around your business
              </span>
            </div>
            <h2 className="font-heading font-black text-2xl md:text-3xl mt-5 tracking-tight text-white">
              No two companies are the same, and neither are our packages.
            </h2>
            <p className="mt-4 text-zinc-300 leading-relaxed">
              Every plan we put together is custom-built around your goals,
              your sector positioning and the channels that move the needle
              for you. Whether you need a single film, a full content engine
              or a long-term authority programme, we&apos;ll shape it around
              your business.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <div className="border border-zinc-800 bg-zinc-950/60 rounded-xl p-5">
                <p className="font-mono text-[10px] tracking-widest text-accent-blue">
                  STARTING FROM
                </p>
                <p className="font-heading font-black text-4xl md:text-5xl mt-2 text-white tracking-tight">
                  £1,499
                </p>
                <p className="text-sm text-zinc-400 mt-2">
                  for a 12-month campaign, fully tailored, fully managed.
                </p>
              </div>
              <div className="border border-zinc-800 bg-zinc-950/60 rounded-xl p-5">
                <p className="font-mono text-[10px] tracking-widest text-accent-blue">
                  WHAT HAPPENS NEXT
                </p>
                <ul className="mt-3 space-y-2 text-sm text-zinc-300">
                  <li className="flex gap-2">
                    <Check className="h-4 w-4 text-accent-blue mt-0.5 shrink-0" />
                    A no-pitch discovery call with the editorial team
                  </li>
                  <li className="flex gap-2">
                    <Check className="h-4 w-4 text-accent-blue mt-0.5 shrink-0" />
                    A tailored 12-month outline against your selections
                  </li>
                  <li className="flex gap-2">
                    <Check className="h-4 w-4 text-accent-blue mt-0.5 shrink-0" />
                    Transparent pricing, no agency lock-in
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <Link
              to="/what-we-do"
              data-testid="success-explore-services"
              className="bg-primary-blue text-white px-6 py-3 text-sm font-bold uppercase tracking-widest hover:glow-blue transition-all rounded-full"
            >
              Explore what we do →
            </Link>
            <Link
              to="/case-studies"
              data-testid="success-case-studies"
              className="border border-zinc-700 hover:border-accent-blue px-6 py-3 text-sm font-bold uppercase tracking-widest transition-colors rounded-full"
            >
              See case studies
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main
      data-testid="positioning-review-page"
      className="bg-white pt-28 pb-20 min-h-screen"
    >
      <Seo
        title="Positioning Review"
        description="Book a Positioning Review with HighwaysIndustry.com. A structured audit of your brand, market position and content strategy in the UK highways and infrastructure sector."
        path="/positioning-review"
      />
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-accent-blue" />
              <span className="label-eyebrow text-accent-blue">
                Discovery Call · 4 Quick Questions
              </span>
            </div>
            <h1 className="font-heading font-black text-4xl md:text-6xl mt-4 leading-[0.95] tracking-tighter text-slate-900">
              Tell us about your business.
              <br />
              <span className="text-slate-400">
                We&apos;ll build the strategy.
              </span>
            </h1>
            <p className="mt-6 text-slate-700 leading-relaxed">
              4 quick questions, pick as many answers as apply. Then a
              no-pitch discovery call with the editorial team. We&apos;ll
              come back to you with a tailored 12-month outline, built
              around your business.
            </p>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">
              <span className="text-accent-blue font-semibold">Heads-up:</span> no two
              companies are the same, every package is custom-built.
              Campaigns start from <strong className="text-slate-900">£1,499</strong> for 12 months.
            </p>
          </div>

          {/* Stepper */}
          <div className="lg:col-span-7 lg:pt-4">
            <div className="grid grid-cols-4 gap-2">
              {STEPS.map((s, i) => (
                <div
                  key={s.key}
                  data-testid={`step-indicator-${s.key}`}
                  className={`border-t-2 pt-3 ${
                    i <= step ? "border-accent-blue" : "border-slate-200"
                  }`}
                >
                  <p
                    className={`font-mono text-[10px] tracking-widest ${
                      i <= step ? "text-accent-blue" : "text-slate-400"
                    }`}
                  >
                    0{i + 1}
                  </p>
                  <p
                    className={`text-xs md:text-sm font-semibold mt-1 ${
                      i <= step ? "text-slate-900" : "text-slate-400"
                    }`}
                  >
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Step content */}
        <div className="mt-12 lg:mt-16 border border-slate-200 bg-slate-50 p-6 md:p-10 lg:p-14 rounded-2xl shadow-sm">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {step === 0 && (
                <StepPillar
                  values={data.pillar}
                  onToggle={(v) => toggle("pillar", v)}
                />
              )}
              {step === 1 && (
                <StepChoice
                  title="Where do you currently sit in the market?"
                  description="Pick all that apply. We use this to calibrate the programme intensity."
                  options={MARKET_POSITIONS}
                  values={data.market_position}
                  onToggle={(v) => toggle("market_position", v)}
                  testidPrefix="market"
                  stepNum="02"
                />
              )}
              {step === 2 && (
                <StepChoice
                  title="What are your biggest roadblocks to authority?"
                  description="Pick all that apply. The honest answers here shape the first 90 days of the plan."
                  options={ROADBLOCKS}
                  values={data.roadblock}
                  onToggle={(v) => toggle("roadblock", v)}
                  testidPrefix="roadblock"
                  stepNum="03"
                />
              )}
              {step === 3 && (
                <StepLead data={data} setVal={setVal} />
              )}
            </motion.div>
          </AnimatePresence>

          {/* Nav */}
          <div className="mt-12 flex items-center justify-between border-t border-slate-200 pt-6 gap-4">
            <button
              type="button"
              onClick={back}
              disabled={step === 0}
              data-testid="step-back"
              className="inline-flex items-center gap-2 py-3 pr-3 text-sm font-bold uppercase tracking-widest text-slate-500 hover:text-accent-blue disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={next}
                disabled={!canProceed()}
                data-testid="step-next"
                className="inline-flex items-center gap-2 bg-primary-blue text-white px-7 py-4 text-sm font-bold uppercase tracking-widest hover:glow-blue transition-all disabled:opacity-40 disabled:cursor-not-allowed rounded-full"
              >
                Next <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={!canProceed() || loading}
                data-testid="step-submit"
                className="inline-flex items-center gap-2 bg-primary-blue text-white px-7 py-4 text-sm font-bold uppercase tracking-widest hover:glow-blue transition-all disabled:opacity-40 disabled:cursor-not-allowed rounded-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending
                  </>
                ) : (
                  <>
                    Get my 12-month outline <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

function StepPillar({ values, onToggle }) {
  return (
    <div>
      <p className="font-mono text-xs text-accent-blue">STEP 01</p>
      <h2 className="font-heading font-black text-3xl md:text-5xl mt-3 leading-tight tracking-tighter text-slate-900">
        What do you want to be known for?
      </h2>
      <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
        Pick one, or pick several. Many of our most successful clients own
        more than one position (e.g. Safety + Sustainability).
      </p>

      <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {POSITIONS.map((p, i) => {
          const active = values.includes(p.value);
          return (
            <button
              key={p.value}
              type="button"
              onClick={() => onToggle(p.value)}
              data-testid={`step-pillar-${p.value}`}
              aria-pressed={active}
              className={`text-left p-5 border-2 rounded-xl transition-all bg-white ${
                active
                  ? "border-accent-blue glow-cyan"
                  : "border-slate-200 hover:border-slate-400"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs text-accent-blue">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {active && <Check className="h-4 w-4 text-accent-blue" />}
              </div>
              <p className="font-heading font-bold text-xl mt-5 tracking-tight text-slate-900">
                {p.label}
              </p>
            </button>
          );
        })}
      </div>

      {values.length > 0 && (
        <p className="mt-6 text-sm text-slate-500">
          <span className="text-accent-blue font-semibold">{values.length}</span>{" "}
          selected, pick more if they apply, or hit Next.
        </p>
      )}
    </div>
  );
}

function StepChoice({ title, description, options, values, onToggle, testidPrefix, stepNum }) {
  return (
    <div>
      <p className="font-mono text-xs text-accent-blue">STEP {stepNum}</p>
      <h2 className="font-heading font-black text-3xl md:text-5xl mt-3 leading-tight tracking-tighter text-slate-900">
        {title}
      </h2>
      <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">{description}</p>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-3">
        {options.map((o) => {
          const active = values.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onToggle(o.value)}
              data-testid={`step-${testidPrefix}-${o.value}`}
              aria-pressed={active}
              className={`text-left p-5 border-2 rounded-xl transition-all bg-white ${
                active
                  ? "border-accent-blue glow-cyan"
                  : "border-slate-200 hover:border-slate-400"
              }`}
            >
              <div className="flex items-center justify-between">
                <p className="font-heading font-bold text-lg md:text-xl tracking-tight text-slate-900">
                  {o.label}
                </p>
                {active && <Check className="h-5 w-5 text-accent-blue" />}
              </div>
              <p className="text-sm text-slate-500 mt-3 leading-relaxed">
                {o.desc}
              </p>
            </button>
          );
        })}
      </div>

      {values.length > 0 && (
        <p className="mt-6 text-sm text-slate-500">
          <span className="text-accent-blue font-semibold">{values.length}</span>{" "}
          selected, pick more if they apply, or hit Next.
        </p>
      )}
    </div>
  );
}

function ValueReinforcement() {
  return (
    <div
      data-testid="value-reinforcement"
      className="mt-10 rounded-2xl border border-slate-200 bg-slate-50/70 overflow-hidden"
    >
      <div className="grid md:grid-cols-2">
        {/* Left: what you get */}
        <div className="p-6 md:p-8 border-b md:border-b-0 md:border-r border-slate-200">
          <p className="font-mono text-[10px] tracking-widest text-accent-blue">
            FOR £1,499 / YEAR YOU GET
          </p>
          <ul className="mt-4 space-y-3 text-sm text-slate-800">
            <li className="flex gap-3">
              <span className="text-accent-blue mt-0.5">▸</span>
              <span>
                <strong>12 months of content</strong> built around your
                positioning, all in-house.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="text-accent-blue mt-0.5">▸</span>
              <span>
                <strong>Distribution</strong> to 74K monthly readers, 16K
                newsletter subscribers and 300K+ social followers.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="text-accent-blue mt-0.5">▸</span>
              <span>
                Video, photography, writing, design, social, all under one
                roof.
              </span>
            </li>
          </ul>
        </div>

        {/* Right: what it usually costs */}
        <div className="p-6 md:p-8">
          <p className="font-mono text-[10px] tracking-widest text-slate-500">
            WHAT VISIBILITY USUALLY COSTS
          </p>
          <ul className="mt-4 space-y-3 text-sm text-slate-700">
            <li className="flex items-center justify-between gap-3 border-b border-slate-200 pb-3">
              <span>Single trade-show stand</span>
              <strong className="text-slate-900 whitespace-nowrap">
                £8k - £15k
              </strong>
            </li>
            <li className="flex items-center justify-between gap-3 border-b border-slate-200 pb-3">
              <span>In-house marketing hire (annual)</span>
              <strong className="text-slate-900 whitespace-nowrap">
                £45k+
              </strong>
            </li>
            <li className="flex items-center justify-between gap-3">
              <span>Generic B2B agency retainer</span>
              <strong className="text-slate-900 whitespace-nowrap">
                £3k - £10k / month
              </strong>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-200 px-6 md:px-8 py-4 bg-white text-sm text-slate-600 leading-relaxed">
        Marketing agencies create content and hope people see it. We create
        content and distribute it through our own industry audience.{" "}
        <strong className="text-slate-900">
          The moat no marketing agency can replicate.
        </strong>
      </div>
    </div>
  );
}

function StepLead({ data, setVal }) {
  return (
    <div>
      <p className="font-mono text-xs text-accent-blue">STEP 04</p>
      <h2 className="font-heading font-black text-3xl md:text-5xl mt-3 leading-tight tracking-tighter text-slate-900">
        Where should we send your tailored outline?
      </h2>
      <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
        We&apos;ll come back to you within 2 working days with a tailored
        12-month positioning outline, built against your selections.
        Campaigns start from <strong className="text-slate-900">£1,499</strong>{" "}
        for 12 months.
      </p>

      <ValueReinforcement />

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
        <LeadField name="name" label="Full name *" value={data.name} setVal={setVal} />
        <LeadField name="company" label="Company *" value={data.company} setVal={setVal} />
        <LeadField name="email" type="email" label="Work email *" value={data.email} setVal={setVal} />
        <LeadField name="role" label="Role (optional)" value={data.role} setVal={setVal} />
        <LeadField name="phone" label="Phone (optional)" value={data.phone} setVal={setVal} />
        <div className="md:col-span-2">
          <label className="label-eyebrow text-slate-500 block">
            Anything else worth knowing? (optional)
          </label>
          <textarea
            data-testid="step-lead-notes"
            value={data.notes}
            onChange={(e) => setVal("notes", e.target.value)}
            rows={4}
            placeholder="Specific frameworks you're targeting, decision dates, etc."
            className="mt-2 w-full bg-white border border-slate-300 focus:border-accent-blue focus:outline-none p-4 text-base text-slate-900 placeholder-slate-400 rounded-xl"
          />
        </div>
      </div>
    </div>
  );
}

function LeadField({ name, label, value, setVal, type = "text" }) {
  return (
    <div>
      <label className="label-eyebrow text-slate-500 block">{label}</label>
      <input
        data-testid={`step-lead-${name}`}
        type={type}
        value={value}
        onChange={(e) => setVal(name, e.target.value)}
        placeholder={label.replace(" *", "")}
        className="mt-2 w-full bg-white border border-slate-300 focus:border-accent-blue focus:outline-none p-4 text-base text-slate-900 placeholder-slate-400 rounded-xl"
      />
    </div>
  );
}
